import styled, { useTheme } from "styled-components";
import Card from "common/Boxes/Card";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import Col from "antd/es/col";
import OrderCardStatus from "features/trades/TradeDrawer/Orders/OrderCardStatus";
import EditTargetActions from "features/signals/EditSignal/EditTargetActions";
import PreviousValuePopover from "features/trades/EditTrade/PreviosValuePopover";
import { ReactNode, useContext } from "react";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import { StatusWrapper } from "features/trades/TradeDrawer/Orders/OrderCard";
import TextWithPrecision from "common/Text/TextWithPrecision";
import { OrderTypes } from "@shared/features/trades/common/types";
import { OrderStatus, LanguageNameSpaces } from "@shared/types";
import { typography } from "@shared/common/Themes/typography";
import { EditTargetOrZone } from "@shared/features/signals/EditSignal/types";
import useTargetCard from "@shared/features/signals/useTargetCard";
import OrderNumber from "common/Numbering/OrderNumber";
import { getPairFromSymbol } from "@shared/utils/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { orderCard } from "features/trades/NewLook/drawerDetails.ts";

interface TargetCardProps {
  index: number;
  target: EditTargetOrZone;
  targetType: OrderTypes;
  columnSpans: number[];
  isMobile?: boolean;
}

interface TargetParts {
  id: string;
  number: ReactNode;
  ratio: ReactNode;
  price: ReactNode;
  status: ReactNode;
  editActions: ReactNode;
  columnSpans: number[];
}

type TargetCardHelpers = ReturnType<typeof useTargetCard>;

interface TargetRatioProps {
  popupsContainerId: string;
  ratioChanged: boolean;
  getRatio: TargetCardHelpers["getRatio"];
}

interface TargetPriceProps {
  target: EditTargetOrZone;
  isEdit?: boolean;
  popupsContainerId: string;
  getPriceData: TargetCardHelpers["getPriceData"];
}

type TargetPriceValue = string | [string, string];

interface TargetContext extends TargetCardProps {
  id: string;
  isEdit?: boolean;
  helpers: TargetCardHelpers;
  numberInStatusRow: boolean;
}

const TargetCard = (props: TargetCardProps) => {
  const { isNewLook } = useTheme();
  const parts = useTargetParts(props);
  if (!isNewLook) return <ClassicTargetCard {...parts} />;
  return props.isMobile ? (
    <NewLookTargetCard {...parts} />
  ) : (
    <NewLookTargetRow {...parts} />
  );
};

export default TargetCard;

const useTargetParts = (props: TargetCardProps): TargetParts => {
  const { isNewLook } = useTheme();
  const { isEdit } = useContext(EditSignalContext)!;
  const helpers = useTargetCard({ target: props.target, isEdit });
  return buildTargetParts({
    ...props,
    id: `ratio-container-${props.index}`,
    isEdit,
    helpers,
    numberInStatusRow: isNewLook && !!props.isMobile,
  });
};

const buildTargetParts = (context: TargetContext): TargetParts => ({
  id: context.id,
  number: renderTargetNumber(context),
  ratio: renderTargetRatio(context),
  price: renderTargetPrice(context),
  status: renderTargetStatus(context),
  editActions: renderTargetEditActions(context),
  columnSpans: context.columnSpans,
});

const renderTargetNumber = ({ index, numberInStatusRow }: TargetContext) => (
  <OrderNumber
    displayNumber={index + 1}
    size="l"
    style={numberInStatusRow ? NUMBER_IN_STATUS_ROW : undefined}
  />
);

const renderTargetRatio = ({ id, helpers }: TargetContext) => (
  <TargetRatio
    popupsContainerId={id}
    ratioChanged={helpers.ratioChanged}
    getRatio={helpers.getRatio}
  />
);

const renderTargetPrice = ({ id, target, isEdit, helpers }: TargetContext) => (
  <TargetPrice
    target={target}
    isEdit={isEdit}
    popupsContainerId={id}
    getPriceData={helpers.getPriceData}
  />
);

const renderTargetStatus = ({ target, isEdit }: TargetContext) => (
  <OrderCardStatus status={isEdit ? target.edit_status : target.status} />
);

const renderTargetEditActions = ({
  target,
  isEdit,
  targetType,
  index,
}: TargetContext) =>
  canEditTarget(target, isEdit) && (
    <EditTargetActions
      targetType={targetType}
      targetIndex={index}
      target={target}
    />
  );

const canEditTarget = (target: EditTargetOrZone, isEdit?: boolean) =>
  !!isEdit &&
  target.edit_status !== OrderStatus.CANCELLED &&
  target.status !== OrderStatus.FULFILLED;

const ClassicTargetCard = ({
  id,
  number,
  ratio,
  price,
  status,
  editActions,
  columnSpans,
}: TargetParts) => (
  <Wrapper id={id}>
    <StatusWrapper>
      {status}
      {editActions}
    </StatusWrapper>
    <Flex $alignItems={"center"}>
      {number}
      <Col span={columnSpans[0]} style={CLASSIC_RATIO_STYLE}>
        {ratio}
      </Col>
      <Col span={columnSpans[1]}>
        <Text
          as={"div"}
          type={"secondary"}
          $weight={typography.fontWeights.MEDIUM}
        >
          {price}
        </Text>
      </Col>
    </Flex>
  </Wrapper>
);

const NewLookTargetCard = ({
  id,
  number,
  ratio,
  price,
  status,
  editActions,
  columnSpans,
}: TargetParts) => (
  <NewLookCard id={id}>
    <StatusWrapper>
      {number}
      {status}
      {editActions}
    </StatusWrapper>
    <Flex $alignItems="center">
      <Col span={columnSpans[0]}>{ratio}</Col>
      <Col span={columnSpans[1]}>{price}</Col>
    </Flex>
  </NewLookCard>
);

const NewLookTargetRow = ({
  id,
  number,
  ratio,
  price,
  status,
  editActions,
  columnSpans,
}: TargetParts) => (
  <NewLookRow id={id}>
    <Col span={columnSpans[0]}>
      <Flex $justifyContent="flex-start">
        {number}
        {ratio}
      </Flex>
    </Col>
    <Col span={columnSpans[1]}>{price}</Col>
    <NewLookStatusCell>
      {status}
      {editActions && <NewLookActionsSlot>{editActions}</NewLookActionsSlot>}
    </NewLookStatusCell>
  </NewLookRow>
);

const TargetRatio = ({
  popupsContainerId,
  ratioChanged,
  getRatio,
}: TargetRatioProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const appTheme = useTheme();
  return (
    <PreviousValuePopover
      popupsContainerId={popupsContainerId}
      valueName={t("target_card.ratio")}
      valueChanged={ratioChanged}
      previousValue={
        <Text as={"div"} $weight={typography.fontWeights.SEMI_BOLD}>
          {getRatio(true)}
        </Text>
      }
      value={
        <Text
          as="div"
          $weight={typography.fontWeights.MEDIUM}
          color={ratioChanged ? appTheme.colors.primaryBlue : ""}
        >
          {getRatio()}
        </Text>
      }
      mediumWeight={true}
    />
  );
};

const TargetPrice = ({ target, isEdit, ...props }: TargetPriceProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { signalData } = useContext(EditSignalContext)!;
  const price = isEdit ? target.edit_target_price : target.target_price;
  return (
    <PreviousValuePopover
      popupsContainerId={props.popupsContainerId}
      valueName={t("target_card.price")}
      valueChanged={hasPriceChanged(target)}
      previousValue={renderPriceText(props.getPriceData, target.target_price)}
      value={renderPriceText(props.getPriceData, price)}
      alignBottom={true}
      mediumWeight={true}
      coin={getSignalPair(signalData?.symbol)}
    />
  );
};

const getSignalPair = (symbol?: string) =>
  symbol ? getPairFromSymbol(symbol) : undefined;

const renderPriceText = (
  getPriceData: TargetCardHelpers["getPriceData"],
  targetPrice: TargetPriceValue
) => {
  const [minPrice, maxPrice] = getPriceData(targetPrice);
  if (!maxPrice) return <TextWithPrecision {...minPrice} />;
  return (
    <>
      <TextWithPrecision {...minPrice} />
      {" - "}
      <TextWithPrecision {...maxPrice} />
    </>
  );
};

const hasPriceChanged = (target: EditTargetOrZone) =>
  Array.isArray(target.target_price)
    ? target.target_price.some(
        (price, index) => price != target.edit_target_price[index]
      )
    : target.target_price != target.edit_target_price;

const NUMBER_IN_STATUS_ROW = { position: "absolute", left: 0 } as const;
const CLASSIC_RATIO_STYLE = { flex: "auto" };

const NewLookCard = styled.div`
  ${orderCard};
  position: relative;
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  .ant-typography {
    font-size: inherit;
    color: inherit;
  }
`;
const NewLookRow = styled(NewLookCard)`
  display: flex;
  align-items: center;
  min-height: 52px;
`;
const NewLookStatusCell = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;
const NewLookActionsSlot = styled.div`
  position: relative;
  flex: none;
  width: 24px;
  height: 24px;
`;
const Wrapper = styled(Card)`
  position: relative;
  margin-bottom: 14px;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.theme.isDarkMode &&
    `
   box-shadow: none;
  `};
`;
